// src/components/Chat/components/ChatUI/ChatUI.jsx
import React, { useMemo, useEffect, useRef } from "react";
import { FiX } from "react-icons/fi";
import ChatHeader from "./ChatHeader.jsx";
import ChatMessages from "./ChatMessages";
import ChatInput from "./ChatInput";

const ChatUI = ({
  activeUser,
  currentUser,
  conversations,
  onlineIds,
  socketConnected,
  uploadingFiles,
  uploadProgress,
  fileInputRef,
  messagesEndRef,
  scrollContainerRef,
  onSendMessage,
  onTyping,
  onFileUpload,
  onBack,
  onScroll,
  updateMessageStatus,
  markAllAsRead,
}) => {
  const activeConv = conversations[activeUser.id] || {};
  const onlineSet = useMemo(
    () => new Set((onlineIds || []).map(String)),
    [onlineIds]
  );

  // Group messages by date
  const groupedMessages = useMemo(() => {
    if (!activeConv?.messages) return [];

    const groups = [];
    let currentDate = null;
    let currentGroup = [];

    const formatMessageDate = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "";

      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (d.toDateString() === today.toDateString()) return "Today";
      if (d.toDateString() === yesterday.toDateString()) return "Yesterday";

      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: d.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
      });
    };

    activeConv.messages.forEach((msg, index) => {
      const msgDate = formatMessageDate(msg.createdAt || msg.timestamp);

      if (msgDate !== currentDate) {
        if (currentGroup.length > 0) {
          groups.push({
            date: currentDate,
            messages: [...currentGroup],
          });
        }
        currentDate = msgDate;
        currentGroup = [msg];
      } else {
        currentGroup.push(msg);
      }

      if (index === activeConv.messages.length - 1) {
        groups.push({
          date: currentDate,
          messages: currentGroup,
        });
      }
    });

    return groups;
  }, [activeConv?.messages]);

  // Track previous total messages per user to detect initial load
  const prevTotalMessagesRef = useRef(0);

  // Reset scroll ref when switching users
  useEffect(() => {
    prevTotalMessagesRef.current = 0;
  }, [activeUser?.id]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    try {
      const sc = scrollContainerRef?.current;
      if (!sc) return;

      // Compute total messages count
      const totalMessages = groupedMessages.reduce(
        (sum, g) => sum + (g.messages?.length || 0),
        0
      );

      // If this is the first load for this conversation (previously zero messages), always scroll to bottom
      const prevCount = prevTotalMessagesRef.current || 0;
      if (prevCount === 0 && totalMessages > 0) {
        setTimeout(() => {
          sc.scrollTop = sc.scrollHeight;
        }, 100);
        prevTotalMessagesRef.current = totalMessages;
        return;
      }

      // Otherwise only autoscroll when user is near bottom
      const distanceFromBottom =
        sc.scrollHeight - sc.scrollTop - sc.clientHeight;
      if (distanceFromBottom < 200) {
        setTimeout(() => {
          sc.scrollTop = sc.scrollHeight;
        }, 50);
      }

      prevTotalMessagesRef.current = totalMessages;
    } catch (err) {
      // ignore
    }
  }, [groupedMessages]);

  // Mark all as read when opening chat
  useEffect(() => {
    if (activeUser && activeConv.unread > 0) {
      markAllAsRead?.();
    }
  }, [activeUser, activeConv.unread, markAllAsRead]);

  return (
    <div className="flex flex-col h-full">
      {/* Chat Header */}
      <div className="flex-shrink-0 px-4 py-3.5 bg-gradient-to-r from-yellow-300 to-yellow-200 flex items-center justify-between border-b-2 border-yellow-400 shadow-md">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 text-blue-900 hover:bg-yellow-300 rounded-lg sm:hidden transition-all"
          >
            <FiX className="text-lg font-bold" />
          </button>
          <ChatHeader
            activeUser={activeUser}
            onlineSet={onlineSet}
            typing={activeConv.typing}
            socketConnected={socketConnected}
            currentUser={currentUser}
          />
        </div>

        <div className="flex items-center gap-2">
          <div
            className={`w-2 h-2 rounded-full ${
              socketConnected ? "bg-green-500" : "bg-red-500"
            }`}
          ></div>
          <span className="text-xs text-blue-900 font-semibold">
            {socketConnected ? "Online" : "Offline"}
          </span>
        </div>
      </div>

      {/* Messages Area */}
      <div
        className="flex-1 overflow-y-auto scrollbar-hide bg-gradient-to-b from-white to-blue-50"
        onScroll={onScroll}
        ref={scrollContainerRef}
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23fef9e7' fill-opacity='0.8' fill-rule='evenodd'/%3E%3C/svg%3E")`,
        }}
      >
        <ChatMessages
          groupedMessages={groupedMessages}
          messagesEndRef={messagesEndRef}
          currentUser={currentUser}
          activeUser={activeUser}
          uploadingFiles={uploadingFiles}
          uploadProgress={uploadProgress}
          updateMessageStatus={updateMessageStatus}
        />
      </div>

      {/* Chat Input */}
      <div className="flex-shrink-0">
        <ChatInput
          disabled={!socketConnected}
          onSend={(text) => onSendMessage(text)}
          onTyping={onTyping}
          onFileSelect={(file) => onFileUpload(file)}
          fileInputRef={fileInputRef}
        />
      </div>
    </div>
  );
};

// Inject minimal helper styles
if (typeof document !== "undefined") {
  const customStyles = `
  .scrollbar-hide::-webkit-scrollbar { width: 6px; }
  .scrollbar-hide::-webkit-scrollbar-track { background: transparent; }
  .scrollbar-hide::-webkit-scrollbar-thumb { background: #93c5fd; border-radius: 3px; }
  `;

  if (!document.head.querySelector("style[data-messenger-styles]")) {
    const styleEl = document.createElement("style");
    styleEl.setAttribute("data-messenger-styles", "1");
    styleEl.textContent = customStyles;
    document.head.appendChild(styleEl);
  }
}

export default ChatUI;
